import { useEffect, useState, type ReactElement } from 'react';
import { Check, Copy, Linkedin, Mail, MessageSquare, Send, Share2, Users } from 'lucide-react';
import { referralApi, type ReferralOverview } from '../../lib/api';
import { useToast, errMsg } from '../ToastProvider';
import { Card, PageHeader, Button } from '../ui';

/**
 * Offizielles WhatsApp-Glyph. Lucide fuehrt seit 2023 keine Markenlogos
 * mehr, und eine eigene Icon-Bibliothek nur dafuer waere zu viel — also
 * inline. currentColor, damit es die Textfarbe des Buttons uebernimmt.
 */
function WhatsAppIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 0 1 6.988 2.896 9.82 9.82 0 0 1 2.893 6.993c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.82 11.82 0 0 0 20.465 3.49" />
    </svg>
  );
}

/**
 * Partnerprogramm: persoenlicher Werbelink + Uebersicht der Geworbenen.
 *
 * Der Link traegt einen Code, der beim Klick 60 Tage lang als Cookie liegt.
 * Wer heute klickt und erst naechste Woche registriert, wird trotzdem
 * zugeordnet.
 */
export default function Partner(): ReactElement {
  const toast = useToast();
  const [data, setData] = useState<ReferralOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    referralApi
      .me()
      .then(setData)
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Link aus der AKTUELLEN Domain bauen — so stimmt er auch in der Testumgebung.
  const link = data ? `${window.location.origin}${data.pfad}` : '';

  const nachricht =
    `Ich arbeite mit Saldify – Buchhaltung, Rechnungen und Steuererklärung in einem. ` +
    `Falls du sowas suchst, schau es dir an: ${link}`;

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(link);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      toast.error('Kopieren hat nicht geklappt. Bitte den Link von Hand markieren.');
    }
  }

  type Ziel = 'whatsapp' | 'email' | 'telegram' | 'linkedin' | 'sms' | 'system';

  function teilen(ziel: Ziel) {
    const text = encodeURIComponent(nachricht);
    const url = encodeURIComponent(link);

    switch (ziel) {
      case 'whatsapp':
        // WhatsApp kennt nur EIN Textfeld — der Link steht in der Nachricht.
        window.open(`https://wa.me/?text=${text}`, '_blank', 'noreferrer');
        return;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${url}&text=${encodeURIComponent(nachricht.replace(link, '').trim())}`, '_blank', 'noreferrer');
        return;
      case 'linkedin':
        // LinkedIn zieht Titel und Beschreibung aus den Meta-Tags der Seite,
        // ein Text-Parameter wird ignoriert.
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noreferrer');
        return;
      case 'email': {
        const betreff = encodeURIComponent('Saldify – könnte für dich passen');
        window.location.href = `mailto:?subject=${betreff}&body=${text}`;
        return;
      }
      case 'sms':
        // &body= ist die Schreibweise, die iOS und Android gleichermassen
        // verstehen (iOS akzeptiert auch ?body=, Android nur &).
        window.location.href = `sms:?&body=${text}`;
        return;
      default:
        // Native Auswahl (Handy): deckt Signal, Threema, Instagram-DM usw. ab.
        void navigator.share?.({ title: 'Saldify', text: nachricht, url: link });
    }
  }

  const datum = (s: string) => new Date(s).toLocaleDateString('de-DE');
  const eur = (c: number) => (c / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Partner"
        subtitle="Lade andere zu Saldify ein und behalte den Überblick, wer über dich dazugekommen ist."
      />

      {loading || !data ? (
        <p className="text-gray-500">Lädt …</p>
      ) : (
        <div className="space-y-4">
          {/* Werbelink */}
          <Card className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dein Einladungslink</h3>

            <div className="flex gap-2">
              <input
                readOnly
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
              />
              <Button variant="secondary" onClick={kopieren}>
                {kopiert ? (
                  <span className="flex items-center gap-1.5"><Check className="h-4 w-4" /> Kopiert</span>
                ) : (
                  <span className="flex items-center gap-1.5"><Copy className="h-4 w-4" /> Kopieren</span>
                )}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => teilen('whatsapp')}
                className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2 text-sm font-medium text-white transition hover:brightness-95"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </button>
              <button onClick={() => teilen('email')} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                <Mail className="h-4 w-4" /> E-Mail
              </button>
              <button onClick={() => teilen('telegram')} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                <Send className="h-4 w-4" /> Telegram
              </button>
              <button onClick={() => teilen('linkedin')} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                <Linkedin className="h-4 w-4" /> LinkedIn
              </button>
              {/* SMS lohnt nur mobil — am Rechner oeffnet sich bestenfalls
                  ein ungenutztes Programm. */}
              <button onClick={() => teilen('sms')} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:hidden">
                <MessageSquare className="h-4 w-4" /> SMS
              </button>
              {/* Native Auswahl: nur wo der Browser sie kann (praktisch mobil).
                  Deckt Signal, Threema, Instagram-DM usw. mit ab. */}
              {typeof navigator !== 'undefined' && 'share' in navigator && (
                <button onClick={() => teilen('system')} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                  <Share2 className="h-4 w-4" /> Mehr …
                </button>
              )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Der Link merkt sich deine Empfehlung 60 Tage lang. Wer heute klickt und sich erst
              nächste Woche registriert, wird dir trotzdem zugeordnet.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Für Instagram, Facebook oder deine Website kopierst du den Link und fügst ihn dort
              ein – etwa in Story, Bio oder Direktnachricht.
            </p>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
              <p className="text-xs text-emerald-900 dark:text-emerald-200">
                <strong>Für jede Empfehlung schreiben wir dir einen Monatsbeitrag gut</strong> – in
                Höhe des Tarifs, den dein Kontakt wählt. Das Guthaben wird automatisch mit deiner
                nächsten Rechnung verrechnet. Dein Kontakt bekommt seinen ersten Monat geschenkt.
              </p>
            </div>
          </Card>

          {/* Kennzahlen */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="!p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Eingeladen</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{data.summe.gesamt}</div>
            </Card>
            <Card className="!p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Davon zahlend</div>
              <div className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{data.summe.zahlend}</div>
            </Card>
            <Card className="!p-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Guthaben</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                {eur(data.summe.praemieCents)}
              </div>
            </Card>
          </div>

          {/* Liste */}
          <Card className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Wer über dich dazugekommen ist</h3>

            {data.eingeladene.length === 0 ? (
              <div className="py-6 text-center">
                <Users className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Noch niemand. Teile deinen Link – hier erscheint dann, wer sich darüber registriert.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.eingeladene.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{e.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        seit {datum(e.registriertAm)}
                        {e.plan && e.plan !== 'starter' ? ` · ${e.plan}` : ''}
                      </div>
                    </div>
                    {e.zahltSeit ? (
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        zahlt seit {datum(e.zahltSeit)}
                        {e.praemieCents ? ` · +${eur(e.praemieCents)}` : ''}
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Testphase
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <p className="text-xs text-gray-400 dark:text-gray-500">
            Aus Datenschutzgründen siehst du nur Firmen- bzw. Anzeigenamen und den Status – keine
            E-Mail-Adressen oder Kontaktdaten.
          </p>
        </div>
      )}
    </div>
  );
}
